import { messages } from '@/i18n/catalogs/products/data.cloud';

// GizmoData Cloud launch facts — single source of truth for every sign-up CTA,
// the announcement bar, the homepage hero and the /cloud page. Change the offer
// here (e.g. when the launch offer ends) and every surface follows.

export const CLOUD_APP_URL = 'https://app.gizmodata.com';

// Launch offer: a card-free trial credit (no redemption limit since 2026-10-09). The
// portal's sign-up page reads the code from the link (`?code=`), says "Your $25 launch
// credit is applied automatically. No credit card needed." and grants it when the
// organization is created, so site copy never asks anyone to type the code. A sign-up
// without the code gets no credit: every sign-up link must be CLOUD_SIGNUP_URL.
// Check the live offer: GET https://app.gizmodata.com/api/public/portal-context?code=LAUNCH25
// `active: false` when the offer ends — sign-up links then stop carrying the code.
export const CLOUD_LAUNCH_PROMO = {
  code: 'LAUNCH25',
  creditUsd: 25,
  active: true,
};

// Every "Start free" / sign-up link. While the launch offer is on, it carries the code:
// the portal remembers `?code=` through the sign-in round-trip and applies it to the
// first organization, so nobody has to copy it by hand.
export const CLOUD_SIGNUP_URL = CLOUD_LAUNCH_PROMO.active
  ? `${CLOUD_APP_URL}/sign-up?code=${encodeURIComponent(CLOUD_LAUNCH_PROMO.code)}`
  : `${CLOUD_APP_URL}/sign-up`;

// Regions open to self-serve organizations today (the public-cloud seed in
// gizmodata-nexus-api). Business and Enterprise customers can request more.
export const CLOUD_REGIONS: Array<{ cloud: string; logo: string; regions: Array<{ name: string; code: string }> }> = [
  { cloud: 'AWS', logo: '/logos/aws.svg', regions: [{ name: 'US East (Ohio)', code: 'us-east-2' }] },
  { cloud: 'Google Cloud', logo: '/logos/gcp.svg', regions: [{ name: 'US East (Northern Virginia)', code: 'us-east4' }] },
  { cloud: 'Microsoft Azure', logo: '/logos/azure.svg', regions: [{ name: 'East US 2 (Virginia)', code: 'eastus2' }] },
];

export const CLOUD_REGION_REQUEST_URL = '/contact?from=cloud_region_request';

export const getCloudRegions = (locale: 'en' | 'pt-BR'): typeof CLOUD_REGIONS => CLOUD_REGIONS.map(cloud => ({
  ...cloud, regions: cloud.regions.map(region => ({ ...region, name: (messages[locale] as Record<string, string>)[region.name] ?? region.name })),
}));

// Self-serve plans: /cloud#pricing and the /cloud/*-alternative landing pages both
// render these. `threshold` is the unbilled usage that triggers a mid-month charge.
export const CLOUD_PRICING_TIERS: Array<{
  name: string;
  price: string;
  vcpus: string;
  threshold: string;
  note: string;
  featured?: boolean;
}> = [
  { name: 'Starter', price: '$29', vcpus: '8', threshold: '$100', note: 'Try it on real data. One small cluster, or a few spot ones.' },
  { name: 'Team', price: '$99', vcpus: '32', threshold: '$300', note: 'A team\'s day-to-day analytics with headroom for a big job.', featured: true },
  { name: 'Business', price: '$499', vcpus: '128', threshold: '$1,500', note: 'Multiple environments and heavy workloads, with Sev-1 support that pages our on-call engineer.' },
];
